import { useAddNewVersionsDialog } from "@/domains/documents/hooks/useAddNewVersionsDialog";
import { useVersionExpansion } from "@/domains/documents/hooks/useVersionExpansion";
import type { Document } from "@/types/documents";
import { useCallback, useState } from "react";
import { mapVersionRowToDocumentWithTags } from "./documentTableValues";

import type { documentTableOptions } from "./documentTableOptions";
type Options = Pick<ReturnType<typeof documentTableOptions>, "onAddNewVersions">;
export function useTableDialogState(options: Options) {
  const { onAddNewVersions } = options;
  // Compare dialog state
  const [compareDocuments, setCompareDocuments] = useState<{
    docA: Document | null;
    docB: Document | null;
  }>({ docA: null, docB: null });
  const [compareOpen, setCompareOpen] = useState(false);

  // Child documents modal state
  const [childModalDocId, setChildModalDocId] = useState<string | null>(null);
  const [childModalFilename, setChildModalFilename] = useState<string>("");

  // Teach Franklin modal state
  const [teachFranklinDoc, setTeachFranklinDoc] = useState<Document | null>(null);
  const [teachFranklinOpen, setTeachFranklinOpen] = useState(false);

  // Version history modal state — retained so the "View full history" link
  // inside an expanded version group can still open the summary modal.
  const [versionHistoryState, setVersionHistoryState] = useState<{
    open: boolean;
    versionGroup: string | null;
    filename: string;
  }>({
    open: false,
    versionGroup: null,
    filename: "",
  });

  // Inline version expansion — older versions of a document are fetched on first
  // expansion and rendered as additional rows below the anchor. Shared with the
  // document picker so the two can't drift on what a version chain is.
  const versionExpansion = useVersionExpansion(mapVersionRowToDocumentWithTags);
  const versionDocsCache = versionExpansion.cache;

  /** Open the read-only version history summary modal. */
  const handleShowVersionHistoryModal = useCallback(
    (versionGroup: string | null, filename: string) => {
      if (!versionGroup) return;
      setVersionHistoryState({
        open: true,
        versionGroup,
        filename,
      });
    },
    []
  );

  /**
   * "Add new version" row action — opens a dialog where the user stages files
   * and/or existing documents and orders them. The dialog owns staging and
   * ordering; this table only owns which document is being targeted. Lineages
   * the commit changed drop their cached version chains.
   */
  const addVersions = useAddNewVersionsDialog(onAddNewVersions, versionExpansion.evict);
  return {
    setChildModalDocId,
    setChildModalFilename,
    setTeachFranklinDoc,
    setTeachFranklinOpen,
    versionExpansion,
    versionDocsCache,
    setCompareDocuments,
    setCompareOpen,
    addVersions,
    handleShowVersionHistoryModal,
    compareDocuments,
    compareOpen,
    childModalDocId,
    childModalFilename,
    teachFranklinDoc,
    teachFranklinOpen,
    versionHistoryState,
    setVersionHistoryState,
  };
}
